/**
 * エリアグループラベル群のライフサイクル管理
 *
 * グループ分けの結果とラベルの保持・件数の数え直し・ズームによる表示の切り替えを持つ
 * 店舗データの取得・絞り込みの判定・設定の解決は持たない
 */

import { groupPoints } from '@/points/arealabels/areaGrouping.ts'
import { AreaLabelMarker } from '@/points/arealabels/AreaLabelMarker.ts'
import type { AreaMembership } from '@/points/arealabels/AreaMembership.ts'

/**
 * どのエリアにも属さない店舗の住所を警告に含める件数
 *
 * どの住所が漏れているかの見当が付く程度に出し、店舗数が多い組織でコンソールを埋めない
 */
const UNMATCHED_LOG_LIMIT = 5

export class AreaLabelManager {
  private _map: google.maps.Map

  /**
   * 件数が0のラベルを隠す設定か
   *
   * 組織の設定 area_labels.hide_empty を呼び出し側が解決して渡す
   * いま隠しているかは件数によるので、_isHiddenAsEmpty で判定する
   */
  private _shouldHideOnEmpty: boolean

  /**
   * 判定に使うフィールドのキー
   *
   * 属さない店舗の警告に含めるので、振り分けたあとも持ち続ける
   */
  private _targetField = ''

  /**
   * ラベルがクリックされたときの通知先
   *
   * 寄せる範囲は初期ズームやピンを隠すズームから決まるので、地図をどう動かすかは設定を解決する services/AreaLabels.ts が持つ
   * ここは地図をラベルの置き場として持つだけにして、地図を動かす処理は受け取ったものに任せる
   */
  private _onLabelClicked: (membership: AreaMembership) => void

  /**
   * ラベルのマーカー
   *
   * どの割り当てのラベルかはマーカー自身が持つので、対応を別に持たない
   */
  private _markers: AreaLabelMarker[] = []

  /**
   * ラベル群を出すか
   *
   * 件数の更新とズームの変更のどちらからでも同じ表示になるよう、判定の結果を持つ
   * 件数が決まる前に出さないよう、初期値は false にする
   */
  private _isVisible = false

  /**
   * @param map ラベルを載せる地図
   * @param options hideEmpty は件数が0のラベルを隠すか
   *   設定の解決は呼び出し側が持つ
   */
  constructor(map: google.maps.Map, options: {
    hideEmpty: boolean
    onLabelClicked: (membership: AreaMembership) => void
  }) {
    this._map = map
    this._shouldHideOnEmpty = options.hideEmpty
    this._onLabelClicked = options.onLabelClicked
  }

  /**
   * 全店舗をエリアへ振り分けてラベルを作る
   *
   * この時点ではまだ地図に出さない
   * 絞り込み後の件数が決まっていないので、updateCounts と updateVisibility を待つ
   *
   * どのエリアにも属さない店舗があれば警告を出す
   * ラベルの件数に入らないまま黙って落ちると、定義漏れに気づけない
   *
   * @param points 振り分ける対象の店舗
   * @param areas 振り分け先のエリア、設定の解決は呼び出し側が持つ
   * @param targetField 判定に使うフィールドのキー
   */
  setPoints(points: Point[], areas: AreaLabelArea[], targetField: string) {
    this.clear()

    this._targetField = targetField

    const { memberships, unmatched } = groupPoints(points, areas, targetField)

    this._markers = memberships.map(membership => new AreaLabelMarker(this._map, membership, {
      label: membership.area.label,
      onClicked: this._onLabelClicked
    }))

    if (unmatched.length > 0) {
      this._warnUnmatched(unmatched)
    }
  }

  /**
   * 絞り込み後の件数を数え直して表示に反映する
   *
   * @param filteredPoints 絞り込みを通った店舗
   *   絞り込みの判定はここでは行わない
   *   ラベルはピンを出さないズーム域で出すので、ズームによる表示制限を含まないものを渡す必要がある
   */
  updateCounts(filteredPoints: Point[]) {
    const shownIds = new Set(filteredPoints.map(point => point.id))

    this._markers.forEach(marker => {
      marker.membership.updateShown(shownIds)
      marker.setCount(marker.membership.shownCount)
    })

    this._applyVisibility()
  }

  /**
   * ラベル群を出すかを切り替える
   *
   * 地図の移動ごとに呼ばれるので、件数は数え直さない
   *
   * @param isVisible ラベルを出すズーム域に居るか、ズーム閾値の判定は設定を解決する呼び出し側が持つ
   */
  setVisible(isVisible: boolean) {
    this._isVisible = isVisible
    this._applyVisibility()
  }

  /**
   * 地図に出しているラベルのエリア
   *
   * 表示範囲の外にあるラベルも含む
   * 表示を切っている間は空を返す
   */
  getShownAreas(): AreaLabelArea[] {
    return this._shownMarkers.map(marker => marker.membership.area)
  }

  /**
   * ラベルを全て地図から取り除く
   */
  clear() {
    this._markers.forEach(marker => marker.remove())

    this._markers = []
  }

  /**
   * 地図に出すラベルのマーカー
   *
   * 地図への反映と、案内に渡すエリアの両方がこの結果を使う
   */
  private get _shownMarkers(): AreaLabelMarker[] {
    if (!this._isVisible) {
      return []
    }

    return this._markers.filter(marker => !this._isHiddenAsEmpty(marker))
  }

  /**
   * どのエリアにも属さない店舗を警告する
   *
   * 判定に使ったフィールドを添える
   * 組織の設定で切り替えられるので、住所を見ているとは限らない
   */
  private _warnUnmatched(unmatched: Point[]) {
    console.warn(
      `[AreaLabels] どのエリアにも属さない店舗が ${unmatched.length} 件あります。`
      + `ラベルの件数に入らないので、判定に使うフィールド（${this._targetField}）の値を確かめて`
      + 'バックエンドのエリアの定義を足してください。',
      unmatched.slice(0, UNMATCHED_LOG_LIMIT).map(point => `${point.name} ${point.address}`)
    )
  }

  /**
   * 保持している件数と表示の切り替えから各ラベルの表示を決める
   */
  private _applyVisibility() {
    const shownMarkers = new Set(this._shownMarkers)

    this._markers.forEach(marker => {
      marker.setVisible(shownMarkers.has(marker))
    })
  }

  /**
   * 件数が0なので隠しているラベルか
   */
  private _isHiddenAsEmpty(marker: AreaLabelMarker): boolean {
    return this._shouldHideOnEmpty && marker.membership.isEmpty
  }
}
